import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
    setBusy(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#070707] text-white" data-testid="reset-page-no-token">
        <PublicNav />
        <div className="max-w-md mx-auto px-6 py-12 text-center">
          <h1 className="font-display text-3xl uppercase text-[#ff3b30]">Missing reset link</h1>
          <p className="mt-3 text-[#bbb]">This link is invalid. Request a new one below.</p>
          <Link to="/forgot-password" className="mt-5 inline-block bg-[#d7ff00] text-black font-display tracking-wider uppercase px-6 py-3 hover:bg-white" data-testid="goto-forgot">
            Request reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid="reset-password-page">
      <PublicNav />
      <div className="max-w-md mx-auto px-6 py-12">
        <div className="font-mono-jp text-xs text-[#d7ff00] tracking-[0.3em] uppercase">SET NEW PASSWORD</div>
        <h1 className="mt-3 font-display text-5xl uppercase">Choose a <span className="text-[#d7ff00]">new password</span></h1>

        {done ? (
          <div className="mt-8 bg-[#0a0a0a] border border-[#d7ff00] p-6 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-[#d7ff00] mx-auto" />
            <h2 className="mt-3 font-display text-2xl uppercase">Password updated</h2>
            <p className="mt-2 text-[#bbb] text-sm">Redirecting to login…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1"><Lock className="w-3 h-3" /> New password</label>
              <input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
                className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3" data-testid="reset-password" />
            </div>
            <div>
              <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Confirm new password</label>
              <input type="password" required minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)}
                className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3" data-testid="reset-confirm" />
            </div>
            <button type="submit" disabled={busy} className="w-full bg-[#d7ff00] text-black font-display tracking-wider uppercase text-xl py-4 hover:bg-white disabled:opacity-50" data-testid="reset-submit">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
